'use client';

import React, { useState } from 'react';
import { Container, Row, Col, Card, CardImg, Modal } from 'react-bootstrap';
import { FaTimes, FaDownload } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import './ImageGallery.css';
import Image from 'next/image';

const bucket = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET;
const getStorageUrl = (mediaId) =>
  `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(
    mediaId
  )}?alt=media`;

const sanitizeText = (text) => {
  if (!text) return '';

  // Decode common HTML entities
  const htmlEntities = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&agrave;': 'à',
    '&aacute;': 'á',
    '&acirc;': 'â',
    '&atilde;': 'ã',
    '&auml;': 'ä',
    '&aring;': 'å',
    '&aelig;': 'æ',
    '&ccedil;': 'ç',
    '&egrave;': 'è',
    '&eacute;': 'é',
    '&ecirc;': 'ê',
    '&euml;': 'ë',
    '&igrave;': 'ì',
    '&iacute;': 'í',
    '&icirc;': 'î',
    '&iuml;': 'ï',
    '&ntilde;': 'ñ',
    '&ograve;': 'ò',
    '&oacute;': 'ó',
    '&ocirc;': 'ô',
    '&otilde;': 'õ',
    '&ouml;': 'ö',
    '&oslash;': 'ø',
    '&ugrave;': 'ù',
    '&uacute;': 'ú',
    '&ucirc;': 'û',
    '&uuml;': 'ü',
    '&yacute;': 'ý',
    '&thorn;': 'þ',
    '&yuml;': 'ÿ',
  };

  let decoded = text;

  // Replace HTML entities
  Object.entries(htmlEntities).forEach(([entity, char]) => {
    decoded = decoded.replace(new RegExp(entity, 'g'), char);
  });

  // Remove any remaining HTML tags
  return decoded.replace(/<[^>]*>/g, '');
};

const ImageGallery = ({ images }) => {
  const [show, setShow] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleClose = () => setShow(false);
  const handleShow = (image) => {
    setSelectedImage(image);
    setShow(true);
  };

  const handleDownload = () => {
    if (selectedImage) {
      const link = document.createElement('a');

      // direct download link in new tab
      link.href = getStorageUrl(selectedImage.media_id);
      link.download = sanitizeText(selectedImage.caption);
      link.click();
    }
  };

  if (!Array.isArray(images) || images.length === 0) {
    return null; // Return null if images is not an array or is empty
  }

  const fixedHeight = 300;
  return (
    <>
      <Container className="image-gallery-container">
        <Row className="justify-content-center g-3">
          {images.map((image, index) => (
            <Col
              data-aos="fade-up"
              data-aos-offset="5"
              // delay each image by 100ms
              data-aos-delay={200 * index}
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={index}
            >
              <Card className="image-card" onClick={() => handleShow(image)}>
                <div
                  className="image-container-gallery"
                  style={{ height: `${fixedHeight}px` }}
                >
                  <CardImg
                    data-aos="slide-up"
                    data-aos-offset="5"
                    data-aos-delay={300 * index}
                    variant="top"
                    src={getStorageUrl(image.media_id)}
                    alt={sanitizeText(image.caption)}
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      height: '100%',
                    }}
                  />
                  <div
                    data-aos="fade-up"
                    data-aos-offset="5"
                    data-aos-delay={400 * index}
                    className="caption-overlay"
                  >
                    <Card.Text className="caption-text">
                      {sanitizeText(image.caption)}
                    </Card.Text>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body>
          <div className="modal-image-container">
            <Image
              height={720}
              width={720}
              src={selectedImage && getStorageUrl(selectedImage.media_id)}
              alt={selectedImage && sanitizeText(selectedImage.caption)}
              className="modal-image"
            />
            <div className="modal-header-icons">
              <FaDownload onClick={handleDownload} className="modal-icon" />
              <FaTimes onClick={handleClose} className="modal-icon" />
            </div>
            <div className="modal-caption">
              <p>{selectedImage && sanitizeText(selectedImage.caption)}</p>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ImageGallery;
